import { Fragment, useContext, useRef, useState } from 'react';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import {
  Box,
  Button,
  createTheme,
  Modal,
  TextField,
  ThemeProvider,
  Typography
} from '@mui/material';
import { hiJackColors } from 'constants/colorConstants';
import GlobalContext from 'contexts/GlobalContext';
import { useAppSelector } from 'hooks';
import { useTableScopedOpen } from 'hooks/useTableScopedOpen';
import { useTableSelector } from 'hooks/useTableSelector';
import useGameService from 'services/GameService';

const PrivateTablePasswordModal = () => {
  const isMaintenance = useAppSelector((state) => state.maintenanceSchedule.isMaintenance);
  const privateTable = useTableSelector((table) => table.game.privateTable);
  const tableType = useTableSelector((table) => table.game.tabletype);
  const canJoinPrivateTable = useTableSelector((table) => table.game.canJoinPrivateTable);

  const { verifyPrivateTablePassword } = useGameService();
  const { openAskPassword, setOpenAskPassword, askPasswordMsg, setAskPasswordMsg } =
    useContext(GlobalContext);
  const [password, setPassword] = useState(Array(6).fill(''));
  const firstInputRef = useRef<HTMLInputElement>(null);
  // openAskPassword is an app-wide GlobalContext flag and this modal portals to
  // <body>, so every open table's panel rendered it — the prompt for one table
  // showed up on the table the player swiped to. Scope it to its own table.
  const isOpen = useTableScopedOpen(!!openAskPassword && !!canJoinPrivateTable);

  const handleClose = () => {
    setPassword(Array(6).fill(''));
    setAskPasswordMsg('');
    setOpenAskPassword(false);
  };

  const handleChange = (e: any, idx: any) => {
    const val = e.target.value.slice(-1).replace(/[^a-zA-Z0-9]/g, '');
    const newValues = [...password];
    newValues[idx] = val;
    setPassword(newValues);

    if (val && idx < 5) {
      const nextInput = document.getElementById(`char-${idx + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  const handleKeyDown = (e: any, idx: any) => {
    if (e.key === 'Backspace') {
      e.preventDefault();

      const newValues = [...password];
      if (password[idx]) {
        newValues[idx] = '';
        setPassword(newValues);
      } else if (idx > 0) {
        const prevInput = document.getElementById(`char-${idx - 1}`);
        if (prevInput) prevInput.focus();
        newValues[idx - 1] = '';
        setPassword(newValues);
      }
    }
  };

  const handleCloseAskBuyin = () => {
    verifyPrivateTablePassword(password.join(''));
    setPassword(Array(6).fill(''));
    setAskPasswordMsg('');
    if (firstInputRef.current) {
      firstInputRef.current.focus();
    }
  };

  return (
    <>
      {!isMaintenance && privateTable && tableType === 's' && (
        <Modal open={isOpen}>
          <Box sx={{ ...modalStyle, boxShadow: 10 }}>
            <Typography
              id='modal-modal-title'
              variant='h5'
              component='h2'
              sx={{ textAlign: 'center', marginBottom: '2rem' }}>
              <VpnKeyIcon
                sx={{ textAlign: 'center', color: hiJackColors.orange, fontSize: '3rem' }}
              />
              <br />
              Enter passcode
            </Typography>
            <Typography
              id='modal-modal-title'
              sx={{
                textAlign: 'center',
                marginBottom: '2rem',
                color: askPasswordMsg !== '' ? 'red' : 'white'
              }}
              dangerouslySetInnerHTML={{
                __html:
                  askPasswordMsg !== ''
                    ? askPasswordMsg
                    : `You've been invited to this private table. <br/> Enter the passcode to join.`
              }}></Typography>
            <Box display='flex' alignItems='center' justifyContent='center' gap={1}>
              {password.map((char, idx) => (
                <Fragment key={idx}>
                  {idx === 3 && (
                    <Box component='span' mx={1}>
                      -
                    </Box>
                  )}
                  <TextField
                    id={`char-${idx}`}
                    value={char}
                    onChange={(e) => handleChange(e, idx)}
                    onKeyDown={(e) => handleKeyDown(e, idx)}
                    inputProps={{ maxLength: 1, style: { textAlign: 'center' } }}
                    inputRef={idx === 0 ? firstInputRef : null}
                    sx={{
                      width: 40,
                      '& input': {
                        fontSize: '18px',
                        padding: '10px',
                        backgroundColor: 'white',
                        borderRadius: '10px'
                      }
                    }}
                  />
                </Fragment>
              ))}
            </Box>
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                marginTop: '2rem',
                gap: '2rem'
              }}>
              <ThemeProvider theme={CancelBtnStyle}>
                <Button
                  sx={{ display: 'block', opacity: '.8' }}
                  variant='contained'
                  onClick={handleClose}>
                  Cancel
                </Button>
              </ThemeProvider>
              <ThemeProvider theme={BuyInBtnStyle}>
                <Button
                  sx={{ display: 'block' }}
                  variant='contained'
                  onClick={handleCloseAskBuyin}
                  disabled={password.some((val) => val === '')}>
                  Join Table
                </Button>
              </ThemeProvider>
            </div>
          </Box>
        </Modal>
      )}
    </>
  );
};

export default PrivateTablePasswordModal;

const modalStyle = {
  position: 'absolute' as const,
  top: '40%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: '#12100c',
  color: 'white',
  padding: '2rem',
  width: 'min(90vw, 360px)',
  borderRadius: '12px',
  border: '1px solid rgba(212, 168, 83, 0.2)',
};

const CancelBtnStyle = createTheme({
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          color: 'white',
          backgroundColor: 'rgba(212, 168, 83, 0.2)',
          '&:hover': {
            color: 'white',
            backgroundColor: 'rgba(212, 168, 83, 0.3)'
          },
          '&:disabled': {
            color: 'white',
            opacity: '0.4'
          },
          fontSize: '1rem',
          fontWeight: '400',
          padding: '0.5rem 1rem',
          minWidth: 'unset',
          textTransform: 'none',
          width: '100%'
        }
      }
    }
  }
});

const BuyInBtnStyle = createTheme({
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          color: 'white',
          backgroundColor: '#d4a853',
          '&:hover': {
            color: 'white',
            backgroundColor: '#b8912e'
          },
          '&:disabled': {
            color: 'white',
            backgroundColor: '#d4a853',
            opacity: '0.4'
          },
          fontSize: '1rem',
          fontWeight: '400',
          padding: '0.5rem 1rem',
          minWidth: 'unset',
          textTransform: 'none',
          width: '100%'
        }
      }
    }
  }
});
